// src/store/auth/authSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { combineEpics, Epic, ofType } from 'redux-observable';
import { of, EMPTY, timer, merge } from 'rxjs';
import { 
  filter, 
  switchMap, 
  map, 
  catchError, 
  takeUntil, 
  debounceTime,
  withLatestFrom,
  tap,
  mergeMap,
  distinctUntilChanged
} from 'rxjs/operators';
import { ajax } from 'rxjs/ajax';
import { UserProfile, JwtPayload, decodeJwtToken, PROFILE_KEY } from '@/utils/jwtHelper';
import { refreshAccessToken } from '@/api/HttpClient';
import { APIHosts } from '@/services/reduxHelpers';
import { Action } from '@reduxjs/toolkit';

import AuthService from "@/features/auth/services/AuthService";
import { message } from "antd";
import i18next from "i18next";
import { RootState } from '@/store/types';

// Types
export interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  userProfile: UserProfile | null;
  decodedToken: JwtPayload | null;
  selectedOrgId: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  lastTokenChange: number; // timestamp để trigger reload
  changePasswordStatus: "idle" | "loading" | "success" | "error" | null;
  changePasswordMessage?: string | null;
}

const initialState: AuthState = {
  accessToken: localStorage.getItem('accessToken'),
  refreshToken: localStorage.getItem('refreshToken'),
  userProfile: null,
  decodedToken: null,
  selectedOrgId: localStorage.getItem('orgId'),
  isAuthenticated: !!localStorage.getItem('accessToken'),
  isLoading: false,
  error: null,
  lastTokenChange: 0,
  changePasswordStatus: null,
  changePasswordMessage: null,
};

// Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Actions
    loginRequest: (state, action: PayloadAction<{ username: string; password: string; captcha?: string; captchaId?: string }>) => {
      state.isLoading = true;
      state.error = null;
    },
    loginSuccess: (state, action: PayloadAction<{ accessToken: string; refreshToken: string }>) => {
      const { accessToken, refreshToken } = action.payload;
      state.accessToken = accessToken;
      state.refreshToken = refreshToken;
      state.isAuthenticated = true;
      state.isLoading = false;
      state.error = null;
      state.lastTokenChange = Date.now();
      
      // Decode token và lưu user profile
      const decoded = decodeJwtToken(accessToken);
      if (decoded) {
        state.decodedToken = decoded;
        if (decoded.profile) {
          try {
            state.userProfile = JSON.parse(decoded.profile);
          } catch (error) {
            console.error('Error parsing profile from token:', error);
          }
        }
      }
      
      // Sync với localStorage
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      if (state.userProfile) {
        localStorage.setItem(PROFILE_KEY, JSON.stringify(state.userProfile));
      }
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
      state.isAuthenticated = false;
    },
    
    // Token refresh
    refreshTokenRequest: (state) => {
      state.isLoading = true;
    },
    refreshTokenSuccess: (state, action: PayloadAction<{ accessToken: string; refreshToken?: string }>) => {
      const { accessToken, refreshToken } = action.payload;
      state.accessToken = accessToken;
      if (refreshToken) {
        state.refreshToken = refreshToken;
      }
      state.isLoading = false;
      state.lastTokenChange = Date.now();
      
      // Update decoded token và profile
      const decoded = decodeJwtToken(accessToken);
      if (decoded) {
        state.decodedToken = decoded;
        if (decoded.profile) {
          try {
            state.userProfile = JSON.parse(decoded.profile);
          } catch (error) {
            console.error('Error parsing profile from refreshed token:', error);
          }
        }
      }
      
      // Sync với localStorage
      localStorage.setItem('accessToken', accessToken);
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
      }
      if (state.userProfile) {
        localStorage.setItem(PROFILE_KEY, JSON.stringify(state.userProfile));
      }
    },
    refreshTokenFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    
    // Organization change
    setSelectedOrg: (state, action: PayloadAction<string>) => {
      state.selectedOrgId = action.payload;
      localStorage.setItem('orgId', action.payload);
    },
    
    // Organization change với refresh token
    changeOrgWithRefresh: (state, action: PayloadAction<string>) => {
      const oldOrgId = state.selectedOrgId;
      state.selectedOrgId = action.payload;
      localStorage.setItem('orgId', action.payload);
      
      // Trigger refresh nếu org thay đổi
      if (oldOrgId !== action.payload) {
        state.isLoading = true;
      }
    },
    
    // Logout
    logout: (state) => {
      state.accessToken = null;
      state.refreshToken = null;
      state.userProfile = null;
      state.decodedToken = null;
      state.selectedOrgId = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.error = null;
      state.lastTokenChange = Date.now();
      
      // Clear localStorage
      localStorage.clear();
    },
    
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
    
    // Manual token update (for external usage)
    updateToken: (state, action: PayloadAction<string>) => {
      state.accessToken = action.payload;
      state.lastTokenChange = Date.now();
      
      const decoded = decodeJwtToken(action.payload);
      if (decoded) {
        state.decodedToken = decoded;
        if (decoded.profile) {
          try {
            state.userProfile = JSON.parse(decoded.profile);
          } catch (error) {
            console.error('Error parsing profile from updated token:', error);
          }
        }
      }
      
      localStorage.setItem('accessToken', action.payload);
      if (state.userProfile) {
        localStorage.setItem(PROFILE_KEY, JSON.stringify(state.userProfile));
      }
    },

    changePassword: (state, action: PayloadAction<{oldPassword: string, newPassword: string}>) => {
      state.changePasswordStatus = "loading";
      state.changePasswordMessage = null;
    },
    changePasswordSuccess: (state, action: PayloadAction<{message: string}>) => {
      state.changePasswordStatus = "success";
      state.changePasswordMessage = action.payload.message;
    },
    changePasswordFailure: (state, action: PayloadAction<{message: string}>) => {
      state.changePasswordStatus = "error";
      state.changePasswordMessage = action.payload.message;
    },
    resetChangePasswordState: (state) => {
      state.changePasswordStatus = 'idle';
      state.changePasswordMessage = undefined;
    },
  },
});

// Export actions
export const {
  loginRequest,
  loginSuccess,
  loginFailure,
  refreshTokenRequest,
  refreshTokenSuccess,
  refreshTokenFailure,
  setSelectedOrg,
  changeOrgWithRefresh,
  logout,
  clearError,
  updateToken,
  changePassword,
  changePasswordSuccess,
  changePasswordFailure,
  resetChangePasswordState
} = authSlice.actions;

// Selectors
export const selectAuth = (state: RootState) => state.auth;
export const selectAccessToken = (state: RootState) => state.auth.accessToken;
export const selectRefreshToken = (state: RootState) => state.auth.refreshToken;
export const selectUserProfile = (state: RootState) => state.auth.userProfile;
export const selectDecodedToken = (state: RootState) => state.auth.decodedToken;
export const selectSelectedOrgId = (state: RootState) => state.auth.selectedOrgId;
export const selectIsAuthenticated = (state: RootState) => state.auth.isAuthenticated;
export const selectIsLoading = (state: RootState) => state.auth.isLoading;
export const selectAuthError = (state: RootState) => state.auth.error;
export const selectLastTokenChange = (state: RootState) => state.auth.lastTokenChange;
export const selectChangePassStatus = (state: RootState) => state.auth.changePasswordStatus;
export const selectChangePassMessage = (state: RootState) => state.auth.changePasswordMessage;

// Epics
type RootEpic = Epic<Action, Action, RootState>;

// Epic 1: Handle login
const loginEpic: RootEpic = (action$, state$) =>
  action$.pipe(
    ofType('auth/loginRequest'),
    switchMap((action: any) => {
      const { username, password, captcha, captchaId } = action.payload;
      const orgId = state$.value.auth.selectedOrgId || localStorage.getItem("orgId") || "08dc17d2-4812-4faf-8da9-4bad064e5582";
      const clientId = localStorage.getItem("clientId") || "CXM";

      const body = new URLSearchParams({
        grant_type: "password",
        scope: "offline_access API",
        client_id: clientId,
        username,
        password,
        remember: "true",
        orgId,
        client_secret: "ConstruxivViewersecret",
        ...(captcha && captchaId ? { captcha, captchaId } : {}),
      }).toString();

      return ajax({
        url: `${APIHosts.Authen}/connect/token`,
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Accept: "application/json",
        },
        body,
        responseType: "json",
      }).pipe(
        map((res: any) => 
          loginSuccess({
            accessToken: res.response.access_token,
            refreshToken: res.response.refresh_token,
          })
        ),
        catchError((error: any) =>
          of(loginFailure(error.response?.error_description || "Login failed"))
        )
      );
    })
  );

// Epic 2: Auto refresh token khi org thay đổi
const orgChangeRefreshEpic: RootEpic = (action$, state$) =>
  action$.pipe(
    ofType('auth/changeOrgWithRefresh'),
    debounceTime(100), // Tránh nhiều requests cùng lúc
    switchMap(() => {
      const { refreshToken } = state$.value.auth;
      if (!refreshToken) {
        return of(refreshTokenFailure('No refresh token available'));
      }

      return refreshAccessToken().pipe(
        map((response: any) => 
          refreshTokenSuccess({
            accessToken: response.accessToken,
            refreshToken: response.refreshToken,
          })
        ),
        catchError((error: any) =>
          of(refreshTokenFailure(error.message || 'Refresh failed'))
        )
      );
    })
  );

// Epic 3: Auto refresh token khi sắp hết hạn
const autoRefreshEpic: RootEpic = (action$, state$) =>
  state$.pipe(
    map(state => state.auth.decodedToken),
    distinctUntilChanged((prev, curr) => prev?.exp === curr?.exp),
    filter((decodedToken): decodedToken is JwtPayload => !!decodedToken && !!decodedToken.exp),
    switchMap((decodedToken) => {
      const now = Date.now() / 1000;
      const timeToExpire = decodedToken.exp - now;
      const refreshTime = Math.max(0, (timeToExpire - 300) * 1000); // Refresh 5 phút trước khi hết hạn

      if (refreshTime <= 0) {
        // Token đã hết hạn hoặc sắp hết hạn
        return of(refreshTokenRequest());
      }

      // Đặt timer để refresh
      return timer(refreshTime).pipe(
        map(() => refreshTokenRequest()),
        takeUntil(
          action$.pipe(
            filter(action => 
              action.type === 'auth/logout' || 
              action.type === 'auth/loginSuccess' ||
              action.type === 'auth/updateToken'
            )
          )
        )
      );
    })
  );

// Epic 4: Handle refresh token request
const refreshTokenEpic: RootEpic = (action$, state$) =>
  action$.pipe(
    ofType('auth/refreshTokenRequest'),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      const { refreshToken } = state.auth;
      if (!refreshToken) {
        return of(refreshTokenFailure('No refresh token available'));
      }

      return refreshAccessToken().pipe(
        map((response: any) => 
          refreshTokenSuccess({
            accessToken: response.accessToken,
            refreshToken: response.refreshToken,
          })
        ),
        catchError((error: any) => {
          // Nếu refresh thất bại, logout
          return merge(
            of(refreshTokenFailure(error.message || 'Refresh failed')),
            of(logout())
          );
        })
      );
    })
  );

// Epic 5: Trigger data reload khi token thay đổi
const dataReloadEpic: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(action => 
      action.type === 'auth/loginSuccess' || 
      action.type === 'auth/refreshTokenSuccess' ||
      action.type === 'auth/updateToken'
    ),
    debounceTime(100),
    tap(() => {
      // Trigger events cho các components khác reload data
      window.dispatchEvent(new CustomEvent('auth:tokenChanged', { 
        detail: { timestamp: Date.now() } 
      }));
    }),
    mergeMap(() => {
      // Có thể dispatch actions để reload data ở đây
      // Ví dụ: reload organizations, projects, etc.
      return EMPTY; // Hoặc return các actions reload data
    })
  );

//Epic 6: Change password
const changePasswordEpic: RootEpic = (action$, state$) => 
  action$.pipe(
    ofType('auth/changePassword'), switchMap((action: ReturnType<typeof changePassword>) =>
      AuthService.changePassword({
        oldPassword: action.payload.oldPassword,
        newPassword: action.payload.newPassword,
      }).pipe(
        map((response) => { 
          const successMsg = i18next?.t?.("profile:changePasswordSuccess") || "Change password success!";
          message.success(successMsg); 
          return changePasswordSuccess({
            message: response?.Message,
          })
        }),
        catchError((error) =>{
          const errorMsg = i18next?.t?.("profile:changePasswordFailed") || "Change password failure!";
          message.error(errorMsg); 
            return of(
              changePasswordFailure({
                message: error?.response?.data?.Message || "CHANGE_PASSWORD_FAILED",        
              }),
            )
        }),
      ),)
  )

// Combine all epics
export const authEpics = combineEpics(
  loginEpic,
  orgChangeRefreshEpic,
  autoRefreshEpic,
  refreshTokenEpic,
  dataReloadEpic,
  changePasswordEpic
);

export default authSlice.reducer;